<script setup lang="ts">
import { ref, useId, onMounted, onUnmounted } from "vue";
import { Info } from "@lucide/vue";

defineProps<{ source: string; stationId: string; timestamp: string }>();
const id = useId();
const root = ref<HTMLElement | null>(null);
const button = ref<HTMLButtonElement | null>(null);
const open = ref(false);
const pinned = ref(false);

/** Keep touch/click details open until dismissed. */
function toggle() {
  pinned.value = !pinned.value;
  open.value = pinned.value;
}

/** Hover previews must not dismiss a panel explicitly opened by tapping. */
function leave() {
  if (!pinned.value) open.value = false;
}

/** Close details when interaction moves outside this control. */
function outside(event: Event) {
  if (!root.value?.contains(event.target as Node)) {
    open.value = false;
    pinned.value = false;
  }
}

/** Escape dismisses details and restores focus to the info button. */
function escape(event: KeyboardEvent) {
  if (event.key !== "Escape" || !open.value) return;
  event.stopPropagation();
  button.value?.focus();
  open.value = false;
  pinned.value = false;
}

onMounted(() => {
  document.addEventListener("pointerdown", outside);
  document.addEventListener("focusin", outside);
  document.addEventListener("keydown", escape);
});
onUnmounted(() => {
  document.removeEventListener("pointerdown", outside);
  document.removeEventListener("focusin", outside);
  document.removeEventListener("keydown", escape);
});
</script>

<template>
  <div
    ref="root"
    class="absolute top-1 left-1 z-30"
    @pointerenter="
      (event) => {
        if (event.pointerType === 'mouse') open = true;
      }
    "
    @pointerleave="leave"
  >
    <button
      ref="button"
      type="button"
      class="grid size-9 place-items-center rounded-full text-text-muted hover:bg-app-elevated hover:text-text-primary focus-visible:outline-2 focus-visible:outline-accent"
      aria-label="Weather source details"
      :aria-expanded="open"
      :aria-controls="id"
      @focus="open = true"
      @click="toggle"
    >
      <Info :size="17" aria-hidden="true" />
    </button>
    <div
      v-if="open"
      :id="id"
      role="region"
      aria-label="Weather source details"
      class="absolute top-full left-0 w-64 max-w-[calc(100vw-40px)] rounded-control border border-app-border bg-app-elevated p-4 text-left text-[13px] leading-relaxed text-text-primary shadow-material"
    >
      <strong class="mb-2 block font-semibold">{{ source }}</strong>
      <dl class="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-2">
        <dt class="text-text-muted">Station ID</dt>
        <dd class="break-words">{{ stationId }}</dd>
        <dt class="text-text-muted">Updated</dt>
        <dd>{{ timestamp }}</dd>
      </dl>
    </div>
  </div>
</template>
